import type { FaqItem } from "./pageContent";

export interface BlogLink {
	label: string;
	href: string;
	description: string;
}

export interface RankedTool {
	rank: string;
	name: string;
	bestFor: string;
	description: string;
	featured?: boolean;
}

export interface BlogSection {
	title: string;
	paragraphs: string[];
	bullets?: string[];
}

export interface BlogPost {
	href: string;
	category: string;
	title: string;
	description: string;
	readTime: string;
	updatedLabel: string;
	datePublished: string;
	dateModified: string;
	heroEyebrow: string;
	intro: string;
	rankedTools?: RankedTool[];
	sections: BlogSection[];
	faqs: FaqItem[];
	resources: BlogLink[];
	sources?: BlogLink[];
	ctaTitle: string;
	ctaDescription: string;
}

export const BLOG_POSTS: BlogPost[] = [
	{
		href: "/blog/best-screen-recording-software-2026/",
		category: "Buyer guide",
		title: "Best Screen Recording Software in 2026: 6 Options Compared",
		description:
			"Compare six screen recorders for local editing, instant sharing, long-form production, streaming, and cross-platform work using current published features.",
		readTime: "9 min read",
		updatedLabel: "Updated August 2026",
		datePublished: "2026-04-24",
		dateModified: "2026-08-30",
		heroEyebrow: "2026 buyer guide",
		intro:
			"There is no universal best screen recorder: the right choice depends on whether you prioritize instant links, local processing, cinematic editing, long-form production, or livestreaming. Disclosure: Clapio publishes this guide, so product claims below are limited to published features and the comparison links to official sources.",
		rankedTools: [
			{
				rank: "Local-first option",
				name: "Clapio",
				bestFor: "Polished local projects on macOS, Windows, and Linux",
				description:
					"Clapio combines screen, webcam, microphone, and system audio capture with built-in editing, zoom effects, cursor polish, annotations, and cleaner exports in one desktop workflow.",
				featured: true,
			},
			{
				rank: "Sharing option",
				name: "Loom",
				bestFor: "Fast recording and instant sharing",
				description:
					"Loom is strong when speed matters most, but it offers less control over visual polish before you share the final result.",
			},
			{
				rank: "Production option",
				name: "Camtasia",
				bestFor: "Long-form tutorial production",
				description:
					"Camtasia remains useful for training-heavy workflows, but it can feel heavier than teams need for fast day-to-day demos and bug reports.",
			},
			{
				rank: "Open-source option",
				name: "Cap",
				bestFor: "Open-source recording with instant cloud share links",
				description:
					"Cap combines local Studio editing with instant share links, open-source code, self-hosting, and team features. Current downloads include macOS, Windows, and an x86_64 Linux .deb.",
			},
			{
				rank: "Streaming option",
				name: "OBS Studio",
				bestFor: "Free advanced capture and livestream setups",
				description:
					"OBS is powerful, but the learning curve and lack of built-in post-production make it slower for polished business communication.",
			},
			{
				rank: "Mac editing option",
				name: "ScreenFlow",
				bestFor: "Mac-only creators who want deeper editing",
				description:
					"ScreenFlow is still a capable Mac option, though cross-platform teams often prefer a workflow that also covers Windows and Linux.",
			},
		],
		sections: [
			{
				title: "How this comparison was assembled",
				paragraphs: [
					"This is a feature-based buyer guide, not a controlled benchmark or independent laboratory test. We compared current official product, download, and pricing pages as of August 30, 2026.",
					"Because Clapio publishes the article, the guide does not award an overall winner. It instead maps each product to the workflow its official materials emphasize.",
				],
				bullets: [
					"Screen, webcam, microphone, and system audio capture",
					"Editing speed after recording",
					"Zoom, cursor polish, and annotation support",
					"Export quality for demos, tutorials, and bug reports",
					"Fit for Mac, Windows, and mixed teams",
				],
			},
			{
				title: "Where Clapio fits",
				paragraphs: [
					"Clapio fits buyers who want local recording and editing, automatic zoom and cursor treatment, on-device Whisper captions, and MP4/GIF export on macOS, Windows, or Linux.",
					"Its main trade-off is sharing: it exports files rather than providing the hosted video libraries, comments, analytics, and instant cloud links that Loom and Cap emphasize.",
				],
				bullets: [
					"Built-in recording and editing in one desktop app",
					"Zoom effects, cursor emphasis, annotations, and framing",
					"Polished MP4 and GIF exports",
					"Cross-platform workflow for Mac, Windows, and Linux",
					"One-time Pro license rather than a recurring subscription",
				],
			},
			{
				title: "Which alternative should you choose instead?",
				paragraphs: [
					"Choose Loom when hosted sharing, comments, analytics, and team libraries matter most. Choose Cap for open-source code, instant links, self-hosting, or team workflows. Choose Camtasia or ScreenFlow for deeper long-form editing, and OBS Studio for advanced scenes and livestreaming.",
					"Choose Clapio when you prefer local projects, on-device captions, cinematic cursor and zoom treatment, cross-platform desktop builds, and a one-time Pro license.",
				],
			},
			{
				title: "What to look for before you buy",
				paragraphs: [
					"The wrong screen recorder usually creates more work after the recording than during it. Before you commit, look beyond capture quality and ask how fast you can edit, annotate, and export a video your audience will actually understand.",
				],
				bullets: [
					"Can it capture screen and audio together without awkward setup?",
					"Can you refine the video without opening another editor?",
					"Does it help the viewer follow the action with zoom and cursor polish?",
					"Will it work across your team's devices?",
				],
			},
		],
		faqs: [
			{
				question: "Which screen recording software should I choose in 2026?",
				answer:
					"Choose by workflow: Loom or Cap for hosted sharing, OBS Studio for streaming and complex scenes, Camtasia or ScreenFlow for long-form editing, and Clapio for local-first cinematic recording with on-device captions across macOS, Windows, and Linux.",
			},
			{
				question: "Is Clapio better than Loom for polished demos?",
				answer:
					"Clapio may fit teams that want a local timeline with zoom, cursor, annotation, caption, and export controls before sharing. Loom may fit better when hosted links, viewer feedback, analytics, or workspace features matter more.",
			},
			{
				question: "Which screen recorder is best for Mac and Windows teams?",
				answer:
					"Cross-platform teams usually benefit from a tool like Clapio that supports Mac, Windows, and Linux while keeping the workflow consistent across devices.",
			},
		],
		resources: [
			{
				label: "Camtasia alternative",
				href: "/camtasia-alternative/",
				description:
					"Compare Clapio's focused local workflow with Camtasia's broader multitrack editor.",
			},
			{
				label: "OBS alternative",
				href: "/obs-alternative/",
				description:
					"Compare Clapio's built-in editing with OBS Studio's recording and streaming focus.",
			},
			{
				label: "Screen recorder with audio",
				href: "/screen-recorder-with-audio/",
				description:
					"Go deeper on narrated walkthroughs, demos, and bug reports with audio capture.",
			},
		],
		sources: [
			{
				label: "Loom pricing and features",
				href: "https://www.loom.com/pricing",
				description: "Official Loom plan and feature comparison.",
			},
			{
				label: "Camtasia product page",
				href: "https://www.techsmith.com/camtasia/",
				description: "Official Camtasia recording and editing features.",
			},
			{
				label: "Cap pricing",
				href: "https://cap.so/pricing",
				description: "Official Cap plans and feature comparison.",
			},
			{
				label: "Cap downloads",
				href: "https://cap.so/download",
				description: "Official current desktop downloads.",
			},
			{
				label: "OBS Studio",
				href: "https://obsproject.com/",
				description: "Official OBS recording, streaming, and platform information.",
			},
			{
				label: "ScreenFlow",
				href: "https://www.telestream.net/screenflow/overview.htm",
				description: "Official ScreenFlow overview.",
			},
		],
		ctaTitle: "Try the top pick for 2026",
		ctaDescription:
			"Download Clapio for Mac, Windows, or Linux and build polished screen recordings without adding extra editing overhead.",
	},
	{
		href: "/blog/how-to-record-screen-and-audio-on-windows-2026/",
		category: "How-to",
		title: "How to Record Screen and Audio on Windows in 2026",
		description:
			"A practical 2026 guide for recording Windows screen, microphone, and system audio together without losing clarity or wasting time in post-production.",
		readTime: "7 min read",
		updatedLabel: "Updated July 2026",
		datePublished: "2026-04-24",
		dateModified: "2026-07-05",
		heroEyebrow: "Windows guide",
		intro:
			"Recording screen and audio on Windows sounds simple until you realize many tools still make you choose between the mic, system sound, or a painful post-production workflow. In 2026, the fastest path is to record all of it together and clean it up before export.",
		sections: [
			{
				title: "Step 1: Choose a recorder that captures both screen and sound",
				paragraphs: [
					"The biggest mistake is starting with a tool that only handles the screen well. For demos and bug reports, you usually need the full picture: visuals, microphone narration, and system audio. That makes it easier for someone else to understand what happened the first time they watch the video.",
				],
			},
			{
				title: "Step 2: Turn on microphone and system audio before recording",
				paragraphs: [
					"Before you hit record, confirm both sources are enabled. If you are demonstrating a product workflow, system sound often contains the context your viewer needs. If you are reporting an issue, alerts, playback, or call audio may matter just as much as your narration.",
				],
				bullets: [
					"Check microphone input levels",
					"Enable system audio capture",
					"Record a 10-second sample before the real take",
				],
			},
			{
				title: "Step 3: Record only the window or area that matters",
				paragraphs: [
					"Tighter framing makes every recording easier to follow. The less noise you show, the less work you need later. If your tool supports cursor emphasis, zoom, or annotations, use them to guide attention instead of hoping the viewer keeps up.",
				],
			},
			{
				title: "Step 4: Clean up the recording before export",
				paragraphs: [
					"A quick trim, a few zoom regions, and better cursor clarity can change whether a video feels professional or disposable. This is where tools like Clapio save time because they keep editing inside the same workflow instead of forcing another app into the process.",
				],
				bullets: [
					"Trim dead space at the start and end",
					"Add zoom to critical clicks or UI states",
					"Export MP4 for full demos or GIF for short loops",
				],
			},
		],
		faqs: [
			{
				question: "Can Windows record screen and system audio at the same time?",
				answer:
					"Yes, but the experience depends on the tool. A recorder like Clapio is useful because it captures screen, microphone, and system audio together in one workflow.",
			},
			{
				question: "What is the easiest way to record screen and audio on Windows?",
				answer:
					"The easiest approach is using a desktop recorder with built-in audio capture and editing so you can avoid stitching the final video together manually.",
			},
		],
		resources: [
			{
				label: "Screen and audio recorder Windows",
				href: "/screen-and-audio-recorder-windows/",
				description: "Read the Windows-focused landing page built around this exact workflow.",
			},
			{
				label: "Best screen recording software in 2026",
				href: "/blog/best-screen-recording-software-2026/",
				description: "See the full ranking and why Clapio is our top pick overall.",
			},
		],
		ctaTitle: "Need a simpler Windows workflow?",
		ctaDescription:
			"Use Clapio to record Windows screen, microphone, and system audio together, then polish the result before export.",
	},
	{
		href: "/blog/best-screen-recorder-for-mac-2026/",
		category: "Mac guide",
		title: "Best Screen Recorder for Mac in 2026: 3 Options Compared",
		description:
			"A 2026 guide to the best screen recorder for Mac, with recommendations for polished demos, tutorials, async updates, and lightweight post-production.",
		readTime: "8 min read",
		updatedLabel: "Updated August 2026",
		datePublished: "2026-04-24",
		dateModified: "2026-08-30",
		heroEyebrow: "Mac buyer guide",
		intro:
			"The right Mac screen recorder depends on whether you want basic capture, a full video editor, or a shorter local workflow for polished demos. Disclosure: Clapio publishes this guide, so the comparison is organized by use case and links to official product sources.",
		rankedTools: [
			{
				rank: "Local-first option",
				name: "Clapio",
				bestFor: "Mac teams who want polished demos without extra editing tools",
				description:
					"Clapio combines screen, webcam, and audio capture with zoom, annotations, on-device captions, and MP4/GIF export in one desktop workflow.",
				featured: true,
			},
			{
				rank: "Built-in option",
				name: "QuickTime Player",
				bestFor: "Very basic screen capture",
				description:
					"QuickTime is convenient for simple recordings, but most teams outgrow it as soon as they need better audio, webcam support, or real editing.",
			},
			{
				rank: "Full-editor option",
				name: "ScreenFlow",
				bestFor: "Mac creators who want a deeper editor",
				description:
					"ScreenFlow remains capable on macOS, though it is a heavier fit than many teams need for repeatable weekly demos and walkthroughs.",
			},
		],
		sections: [
			{
				title: "What Mac users need most in 2026",
				paragraphs: [
					"For most Mac teams, the ideal recorder is not the one with the deepest editing suite. It is the one that makes the whole workflow feel short: capture cleanly, explain clearly, polish fast, and export without friction.",
				],
				bullets: [
					"Apple Silicon and Intel support",
					"Screen, webcam, and audio capture",
					"Fast post-production for demos and tutorials",
					"High-quality exports with less manual cleanup",
				],
			},
			{
				title: "Where Clapio fits",
				paragraphs: [
					"Clapio fits Mac users who want local projects, cursor and zoom treatment, on-device Whisper captions, and file exports without moving the recording into a separate editor or hosted video library.",
				],
			},
			{
				title: "When another tool might fit better",
				paragraphs: [
					"Use QuickTime Player for basic captures with minimal setup. Use ScreenFlow when you need a broader multi-track Mac video editor. Use Clapio when automatic zoom, cursor treatment, local captions, and faster MP4/GIF export match the job.",
				],
			},
		],
		faqs: [
			{
				question: "Which screen recorder should I use on Mac in 2026?",
				answer:
					"Use QuickTime Player for simple capture, ScreenFlow for full multi-track editing, or Clapio for a local recording-and-polish workflow with automatic zoom, cursor treatment, on-device captions, and MP4/GIF export.",
			},
			{
				question: "Is QuickTime enough for Mac screen recording?",
				answer:
					"QuickTime is fine for basic captures, but most buyers eventually need webcam support, better audio capture, editing, and stronger export tools.",
			},
		],
		resources: [
			{
				label: "QuickTime Player alternative",
				href: "/quicktime-player-alternative/",
				description: "See the direct comparison page for Mac buyers leaving QuickTime behind.",
			},
			{
				label: "Screen recorder for Mac",
				href: "/screen-recorder-for-mac/",
				description: "Explore the Mac-focused landing page for demos, tutorials, and walkthroughs.",
			},
		],
		sources: [
			{
				label: "Apple QuickTime Player guide",
				href: "https://support.apple.com/guide/quicktime-player/welcome/mac",
				description: "Official QuickTime Player recording documentation.",
			},
			{
				label: "ScreenFlow product page",
				href: "https://www.telestream.net/screenflow/overview.htm",
				description: "Official ScreenFlow feature overview.",
			},
		],
		ctaTitle: "Try Clapio on your Mac",
		ctaDescription:
			"Download Clapio for Apple Silicon or Intel Mac and create cleaner screen recordings with less editing overhead.",
	},
	{
		href: "/blog/how-to-record-screen-on-linux/",
		category: "How-to",
		title: "How to Record Your Screen on Linux in 2026 (Wayland & X11)",
		description:
			"A practical guide to Linux screen recording in 2026: what works on Wayland vs X11, how to capture system audio and webcam, and how to get polished output — not just raw footage.",
		readTime: "10 min read",
		updatedLabel: "Updated August 2026",
		datePublished: "2026-07-05",
		dateModified: "2026-08-30",
		heroEyebrow: "Linux guide",
		intro:
			"Screen recording on Linux got harder before it got easier. The move from X11 to Wayland broke most of the classic recorders, system audio capture still confuses everyone, and the tools that do work usually stop at raw footage. This guide covers what actually works in 2026 — from the built-in GNOME recorder to OBS to Clapio — and how to end up with a video you would actually share.",
		sections: [
			{
				title: "Why Linux screen recording is still confusing",
				paragraphs: [
					"The core problem is the display server transition. Older recorders like Kazam and SimpleScreenRecorder were built for X11, where any app could read the screen directly. Wayland — now the default session on Ubuntu, Fedora, and most major distros — deliberately blocks that for security, requiring capture to go through PipeWire and desktop portals instead.",
					"The result: a recorder either supports the portal workflow or it silently records a black screen. Before you pick a tool, check which session you are running — log out, click the gear icon on the login screen, and see whether you are on Wayland or Xorg — then make sure your recorder explicitly supports it.",
				],
				bullets: [
					"Wayland sessions need PipeWire/portal-aware recorders",
					"X11-era tools often show a black screen on Wayland",
					"System audio capture is a separate problem from screen capture",
					"Most Linux tools output raw footage with no editing step",
				],
			},
			{
				title: "Option 1: Your desktop's built-in recorder",
				paragraphs: [
					"GNOME ships a basic screen recorder — press Ctrl+Shift+Alt+R and a red dot starts capturing to a WebM file in ~/Videos. KDE Plasma has Spectacle with a similar recording mode. These are fine for a silent capture of a bug you need to attach to a ticket.",
					"The limits arrive fast: no microphone or system audio on GNOME's shortcut recorder, WebM-only output that some tools and ticket systems reject, no webcam, and no editing at all. If anyone besides you will watch the video, you will outgrow this in a day.",
				],
			},
			{
				title: "Option 2: OBS Studio",
				paragraphs: [
					"OBS is the most capable free capture tool on Linux and it fully supports Wayland via PipeWire. If you need scene-based capture, multiple sources, or streaming, it is the right choice.",
					"For everyday demos and tutorials, though, OBS gives you a raw MP4 or MKV and wishes you luck. There is no trimming, no zoom, no cursor emphasis, no captions — you need a separate video editor for all of that, which is exactly the workflow most people are trying to avoid.",
				],
			},
			{
				title: "Option 3: Clapio — recording plus the polish step",
				paragraphs: [
					"Clapio is a native Linux screen recorder that works on both Wayland and X11 and covers the entire workflow: capture screen, webcam, microphone, and system audio together, then edit and export from the same app.",
					"After recording, the timeline editor adds the things Linux capture tools never had — automatic zoom on your clicks, smooth cursor motion, annotations, backgrounds, and device frames. Transcription runs on-device with Whisper, so you can burn in captions or export SRT/VTT subtitles in 16 languages without uploading anything.",
				],
				bullets: [
					"Native Wayland and X11 support — no black screens",
					"Screen + webcam + microphone + system audio in one take",
					"Auto-zoom, cursor smoothing, annotations, device frames",
					"On-device captions with SRT/VTT export in 16 languages",
					"Polished MP4 (up to 4K) or GIF export",
				],
			},
			{
				title: "Step-by-step: recording a polished video on Linux",
				paragraphs: ["Here is the full workflow in Clapio, from install to a shareable file."],
				bullets: [
					"1. Download and install Clapio for Linux from clapio.app",
					"2. Pick what to capture: full screen, a window, or an area",
					"3. Toggle microphone, system audio, and webcam overlay as needed",
					"4. Record your walkthrough in one take — don't worry about small stumbles",
					"5. Trim dead air at the start and end in the timeline",
					"6. Add zoom regions on the clicks that matter (or let auto-zoom place them)",
					"7. Generate captions on-device if the video needs them",
					"8. Export MP4 for demos and tutorials, or GIF for docs and pull requests",
				],
			},
			{
				title: "Getting system audio right on Linux",
				paragraphs: [
					"System audio is where most Linux recording setups fall apart. On modern distros, PipeWire handles both input and output devices, and a recorder that integrates with it can capture your microphone and the desktop's audio in separate, clean streams.",
					"If you are using OBS, you will need to add and monitor audio sources manually. Clapio exposes microphone and system audio as simple toggles, which is one less reason a recording fails halfway through a demo.",
				],
			},
		],
		faqs: [
			{
				question: "How do I record my screen on Wayland?",
				answer:
					"Use a recorder that supports PipeWire and desktop portals. GNOME's built-in recorder (Ctrl+Shift+Alt+R), OBS Studio, and Clapio all work on Wayland. Older X11 tools like Kazam typically show a black screen on Wayland sessions.",
			},
			{
				question: "How do I record screen with system audio on Linux?",
				answer:
					"Pick a recorder with PipeWire audio integration. Clapio captures microphone and system audio with simple toggles on both Wayland and X11; in OBS you can add a desktop audio source manually.",
			},
			{
				question: "Which screen recorder should I use on Linux in 2026?",
				answer:
					"Use OBS Studio for advanced scenes and livestreaming. Use Clapio when you want a local editing workflow with auto-zoom, cursor treatment, on-device captions, and MP4/GIF export on Wayland or X11.",
			},
			{
				question: "Can I record a GIF of my screen on Linux?",
				answer:
					"Yes. Clapio exports recordings as GIFs directly, which is handy for pull requests, documentation, and bug reports where autoplaying video isn't possible.",
			},
		],
		resources: [
			{
				label: "Screen Recorder for Linux",
				href: "/screen-recorder-for-linux/",
				description: "The Linux landing page: cinematic recording natively on Wayland and X11.",
			},
			{
				label: "Screen Studio for Linux",
				href: "/screen-studio-for-linux/",
				description: "Want the Screen.studio look on Linux? Here is the native answer.",
			},
			{
				label: "OBS alternative",
				href: "/obs-alternative/",
				description: "Compare Clapio and OBS when the goal is polished video, not streaming.",
			},
		],
		ctaTitle: "Record polished video on Linux",
		ctaDescription:
			"Download Clapio for Linux and go from Wayland or X11 capture to a captioned, polished MP4 or GIF — all on your machine.",
	},
	{
		href: "/blog/how-to-record-a-gif-of-your-screen/",
		category: "How-to",
		title: "How to Record or Capture a GIF from Your Screen (2026)",
		description:
			"Record or capture a GIF from your screen on Mac, Windows, or Linux. Learn the video-to-GIF workflow for clear, compact demos, docs, and bug reports.",
		readTime: "9 min read",
		updatedLabel: "Updated August 2026",
		datePublished: "2026-07-05",
		dateModified: "2026-08-30",
		heroEyebrow: "GIF guide",
		intro:
			"A screen GIF can show a short UI interaction without narration or a video player. The reliable workflow is to capture a normal screen recording, trim it to the exact moment, then export that clip as an animated GIF. This guide covers that workflow on Mac, Windows, and Linux, including how to convert an existing screen recording and keep the result compact.",
		sections: [
			{
				title: "Quick answer: how to record a GIF from your screen",
				paragraphs: [
					"Record a small region of the screen as video, perform the interaction once, trim away every unnecessary second, and export the finished clip as GIF. For most interface demos, target roughly 3–10 seconds, a modest frame rate, and only the pixels the viewer needs to see.",
					"You can record directly inside an editor such as Clapio or import an existing screen recording and convert the useful section. Recording video first preserves the option to correct timing, add a zoom, and export both GIF and MP4 versions from the same source.",
				],
				bullets: [
					"Mac: capture the application or region, trim it, then export GIF",
					"Windows: record the target window or region, remove pauses, then export GIF",
					"Linux: capture on Wayland or X11, edit the clip locally, then export GIF",
					"Existing video: import the MP4 or screen recording, select the short segment, and convert that segment to GIF",
				],
			},
			{
				title: "Record video first, export GIF second",
				paragraphs: [
					"Tools that record straight to GIF lock you into whatever happened during capture — every hesitation, every wrong click, full length. The better workflow is to record a normal video, trim it down to the moment that matters, then export that clip as a GIF.",
					"This also means one recording can produce both assets: the MP4 for a changelog or tutorial, and a tight GIF of the key interaction for the pull request or doc page.",
				],
			},
			{
				title: "GIF screen recorder workflow for Windows, Mac, and Linux",
				paragraphs: ["The same workflow works identically on macOS, Windows, and Linux."],
				bullets: [
					"1. Record the screen area where the interaction happens — smaller capture area, smaller GIF",
					"2. Perform the interaction once, cleanly",
					"3. Trim the clip to just the interaction — aim for 3–10 seconds",
					"4. Add a zoom region if the click target is small",
					"5. Choose GIF export and pick a size preset (original-size GIF export is available on the Professional plan)",
					"6. Drop the file into your PR, doc, or ticket",
				],
			},
			{
				title: "Keeping GIF file size under control",
				paragraphs: [
					"GIF uses lossless LZW compression, but animation can still become large because the format stores a sequence of indexed-color frames. Screen dimensions, duration, frame rate, motion, and changing colors all affect the final size. The practical goal is to give the encoder fewer pixels and fewer changing frames to store.",
				],
				bullets: [
					"Record a region, not the full screen — a 1280px-wide GIF is usually plenty",
					"Keep it short: every second adds frames, and frames add megabytes",
					"Lower the frame rate for UI demos — 10–15 fps looks fine for cursor movement",
					"Avoid recording video playback or animated backgrounds inside the GIF",
					"If it needs sound or runs past ~15 seconds, it should be an MP4, not a GIF",
				],
			},
			{
				title: "Convert an existing screen recording to GIF",
				paragraphs: [
					"If you already have an MP4, MOV, WebM, or another screen recording, you do not need to record it again. Import the file, select the short section that demonstrates the interaction, crop or zoom to the relevant area, and export that section as GIF.",
					"Keep the original video as the high-fidelity source. If the first GIF is too large, return to the timeline and shorten the selection, reduce its dimensions, lower the frame rate, or remove sections with continuous motion before exporting again.",
				],
				bullets: [
					"Import the existing recording instead of screen-recording the playback",
					"Set precise in and out points around the useful interaction",
					"Crop empty desktop space and emphasize the cursor target",
					"Export GIF, check the final size and legibility, then revise if needed",
				],
			},
			{
				title: "When to use a GIF vs an MP4",
				paragraphs: [
					"Use a GIF for a short, silent interaction that benefits from automatic looping in documentation, chat, or a ticket. Use MP4 when you need audio, longer duration, smoother motion, better color fidelity, or a smaller file for substantial video content.",
					"GitHub's official attachment documentation supports GIF, MP4, MOV, and WebM in issue and pull-request conversations. It lists a 10 MB maximum for images and GIFs, while video limits depend on the repository owner's plan. Check the rules of the exact destination rather than assuming every README, wiki, or ticket system embeds the formats identically.",
				],
			},
		],
		faqs: [
			{
				question: "How do I record a GIF of my screen on Mac?",
				answer:
					"Record the screen area with Clapio for macOS, trim the clip in the timeline, and choose GIF as the export format. The same steps work on Windows and Linux.",
			},
			{
				question: "How do I record a GIF of my screen on Windows?",
				answer:
					"Record the target window or a small screen region, trim the clip to the useful interaction, and export it as GIF. Clapio provides the same record, edit, and GIF-export workflow on Windows, macOS, and Linux.",
			},
			{
				question: "Can I turn an existing screen recording into a GIF?",
				answer:
					"Yes. Import the existing video, set in and out points around a short section, crop or zoom if needed, and export that segment as GIF. Keep the original video as the source so you can revise the dimensions, duration, or frame rate without recording again.",
			},
			{
				question: "Why is my screen recording GIF so large?",
				answer:
					"Usually a combination of full-screen capture, long duration, and high frame rate. Record a smaller region, trim to a few seconds, and drop the frame rate — file size scales with all three.",
			},
			{
				question: "What is the best GIF screen recorder?",
				answer:
					"Any tool that lets you record video first and export GIF after editing. Clapio does this on Mac, Windows, and Linux with trimming, zoom, and size presets built in.",
			},
		],
		sources: [
			{
				label: "GitHub Docs: attaching GIF and video files",
				href: "https://docs.github.com/en/get-started/writing-on-github/working-with-advanced-formatting/attaching-files",
				description: "Official GitHub attachment formats and file-size limits.",
			},
			{
				label: "W3C background on GIF and LZW compression",
				href: "https://www.w3.org/Graphics/PNG/CS-950117.html",
				description: "W3C-hosted background on the GIF format's LZW compression.",
			},
		],
		resources: [
			{
				label: "Screen Recorder with Audio",
				href: "/screen-recorder-with-audio/",
				description: "For the recordings that should be MP4s with narration instead.",
			},
			{
				label: "Best screen recording software in 2026",
				href: "/blog/best-screen-recording-software-2026/",
				description: "Our full ranking of recording tools this year.",
			},
		],
		ctaTitle: "Make GIFs people actually watch",
		ctaDescription:
			"Download Clapio and turn one clean screen recording into a polished MP4 and a tight, small GIF — on Mac, Windows, or Linux.",
	},
	{
		href: "/blog/how-to-add-captions-to-a-screen-recording/",
		category: "How-to",
		title: "How to Add Captions to a Screen Recording (Automatic & Private)",
		description:
			"Compare manual, cloud, and on-device captions, then follow a private workflow to burn subtitles in or export SRT and VTT files.",
		readTime: "8 min read",
		updatedLabel: "Updated August 2026",
		datePublished: "2026-07-05",
		dateModified: "2026-08-30",
		heroEyebrow: "Accessibility guide",
		intro:
			"Captions make a screen recording usable when someone cannot hear the audio, chooses not to play it, or benefits from reading along. Here are three ways to caption a screen recording in 2026, plus a private workflow for footage that should not leave your machine.",
		sections: [
			{
				title: "Why captions are no longer optional",
				paragraphs: [
					"The W3C explains that captions provide a synchronized text version of the speech and non-speech audio needed to understand a video. For prerecorded video whose audio carries meaning, captions are required at WCAG Level A.",
					"For customer-facing video — onboarding, demos, tutorials — captions also signal care. A polished recording with clean subtitles reads as documentation; a silent wall of UI reads as an afterthought.",
				],
				bullets: [
					"Captions make videos accessible to deaf and hard-of-hearing viewers",
					"Captions also help people who process written information better than audio",
					"A separate subtitle file lets compatible players expose a caption toggle",
				],
			},
			{
				title: "The three ways to caption a recording",
				paragraphs: [
					"Manual captioning means transcribing the audio, deciding which meaningful sounds to include, and timing each line. It gives you direct control but can be tedious without dedicated software.",
					"Cloud AI captioning can produce a useful first draft, but it requires uploading the recording or audio to a remote service. Check the provider's retention, privacy, pricing, and offline limitations before using it with sensitive footage.",
					"On-device AI captioning runs a speech-recognition model like Whisper locally. You get the speed of AI transcription with none of the upload — which matters when recordings contain unreleased features, customer data, or internal tooling.",
				],
			},
			{
				title: "Treat automatic captions as a first draft",
				paragraphs: [
					"The W3C warns that automatically generated captions usually need editing before they meet user needs or accessibility requirements. Always review names, technical terms, punctuation, timing, speaker changes, and meaningful sounds before publishing.",
					"WebVTT is the W3C format designed for time-aligned text tracks connected to HTML video. SRT is also widely accepted, but platform support varies, so check the destination before choosing an export format.",
				],
				bullets: [
					"Play the full video once while reading every caption",
					"Correct product names, acronyms, numbers, and negations",
					"Keep each cue readable and synchronized with the speaker",
					"Add meaningful non-speech audio when it affects understanding",
				],
			},
			{
				title: "Step-by-step: automatic captions without the cloud",
				paragraphs: [
					"Here is the on-device workflow in Clapio. It works the same on macOS, Windows, and Linux, and it works offline.",
				],
				bullets: [
					"1. Record your screen with microphone narration",
					"2. Open the recording in the editor and run transcription — Whisper runs on your machine",
					"3. Review the generated captions and fix any product names it hasn't heard before",
					"4. Choose burn-in to render subtitles into the video, or keep them as a separate track",
					"5. Export SRT or VTT files — 16 languages are supported — for players and platforms that take sidecar subtitles",
				],
			},
			{
				title: "Burned-in captions vs SRT/VTT files: which to use",
				paragraphs: [
					"Burned-in captions are part of the pixels — they show everywhere the video plays, including chat previews and GIF-style embeds, but viewers can't turn them off and there is exactly one language.",
					"Sidecar SRT or VTT files keep captions as data: platforms like YouTube let viewers toggle them and pick languages, and you can ship several translations with one video file. The practical rule: burn in for Slack, social, and docs; ship SRT/VTT for YouTube, LMS platforms, and help centers — or do both from the same recording.",
				],
			},
		],
		faqs: [
			{
				question: "How do I automatically add captions to a screen recording?",
				answer:
					"Use a recorder with built-in AI transcription. In Clapio, record with narration, run on-device transcription, review the text, then burn the captions in or export SRT/VTT subtitle files — no cloud upload involved.",
			},
			{
				question: "Can I add captions to a video without uploading it?",
				answer:
					"Yes. On-device transcription tools run the speech model locally. Clapio uses Whisper on your machine, so recordings with sensitive content never leave your computer, and captioning works offline.",
			},
			{
				question: "What languages can captions be exported in?",
				answer:
					"Clapio exports SRT and VTT subtitles in 16 languages, generated by on-device Whisper transcription.",
			},
			{
				question: "Should captions be burned in or kept as a separate file?",
				answer:
					"Burn them in for muted-autoplay surfaces like Slack and social feeds; use SRT/VTT sidecar files for platforms with caption toggles like YouTube. From one recording you can produce both.",
			},
		],
		resources: [
			{
				label: "Loom alternative",
				href: "/loom-alternative/",
				description: "Cloud recorders caption in their cloud. Here is the local-first approach.",
			},
			{
				label: "Cap alternative",
				href: "/cap-alternative/",
				description: "How Clapio's on-device AI compares with Cap's Pro-gated cloud AI.",
			},
			{
				label: "Screen recorder with audio",
				href: "/screen-recorder-with-audio/",
				description: "Good captions start with clean narration and system audio capture.",
			},
		],
		sources: [
			{
				label: "W3C WAI: Captions and subtitles",
				href: "https://www.w3.org/WAI/media/av/captions/",
				description:
					"Accessibility guidance on when captions are needed, caption quality, and common file formats.",
			},
			{
				label: "W3C: WebVTT specification",
				href: "https://www.w3.org/TR/webvtt1/",
				description:
					"The standard for time-aligned text tracks used for captions and subtitles with web video.",
			},
			{
				label: "OpenAI Whisper",
				href: "https://github.com/openai/whisper",
				description: "The official open-source multilingual speech-recognition project.",
			},
		],
		ctaTitle: "Caption without the cloud",
		ctaDescription:
			"Download Clapio and generate a caption draft on-device — review it, burn it in, or export SRT/VTT in 16 languages, entirely on your machine.",
	},
	{
		href: "/blog/screen-studio-vs-cap-vs-clapio/",
		category: "Comparison",
		title: "Screen Studio vs Cap vs Clapio: Cinematic Recorders Compared (2026)",
		description:
			"An honest 2026 comparison of the three cinematic screen recorders — Screen.studio, Cap, and Clapio — across platforms, AI features, editing depth, privacy, and price.",
		readTime: "9 min read",
		updatedLabel: "Updated August 2026",
		datePublished: "2026-07-05",
		dateModified: "2026-08-30",
		heroEyebrow: "Head-to-head",
		intro:
			"Screen Studio, Cap, and Clapio all turn raw capture into a more polished recording, but their platform, sharing, AI, and licensing choices differ. Disclosure: Clapio publishes this comparison. Facts were checked against the competitors' official pages on August 30, 2026, and direct sources appear below.",
		rankedTools: [
			{
				rank: "Best fit",
				name: "Clapio",
				bestFor: "Cross-platform teams and privacy-sensitive recording",
				description:
					"Local-first recording and editing on macOS, Windows, and Linux, with auto-zoom, motion blur, device frames, and on-device Whisper captions.",
			},
			{
				rank: "Best fit",
				name: "Screen.studio",
				bestFor: "Mac-only users who want the original cinematic tool",
				description:
					"A focused macOS recorder with automatic zoom, smooth cursor animation, share links, up-to-4K/60fps export, and transcript generation that runs on the machine.",
			},
			{
				rank: "Best fit",
				name: "Cap",
				bestFor: "Open-source fans who live on instant share links",
				description:
					"An open-source recorder with local Studio editing, instant share links, self-hosting, collaboration, and current macOS, Windows, and Linux downloads.",
			},
		],
		sections: [
			{
				title: "What 'cinematic screen recording' actually means",
				paragraphs: [
					"All three tools solve the same aesthetic problem: raw screen captures look flat and are hard to follow. The fix is a set of automatic effects — zooming into where you click, smoothing the cursor path, padding the recording with a background, framing it in a device mockup — that used to require After Effects and an afternoon.",
					"Where the tools diverge is everything around that core: which platforms they run on, where their AI runs, how deep the editor goes, and what you pay.",
				],
			},
			{
				title: "Platforms: the deciding factor for most teams",
				paragraphs: [
					"Screen Studio's official downloads are for Apple Silicon and Intel Macs. Clapio publishes macOS, Windows, and Linux AppImage builds.",
					"Cap's current download page also offers macOS, Windows beta, and a Linux .deb. Its installation docs describe the Linux package as x86_64 for Debian-compatible distributions.",
				],
			},
			{
				title: "AI features: on-device vs cloud",
				paragraphs: [
					"Clapio runs Whisper transcription and caption generation locally and exports SRT/VTT in 16 languages. Screen Studio also states that transcript generation happens on the machine without sending data to its servers.",
					"Cap lists transcripts, titles, summaries, and chapters with Cap Pro's AI features. Its main product also supports managed cloud sharing and self-hosting, so buyers should compare the exact recording mode and storage configuration they plan to use.",
				],
			},
			{
				title: "Editing depth and output",
				paragraphs: [
					"Clapio includes a timeline with trimming, 6-level zoom, motion blur, annotations, cursor spotlight, 18 backgrounds, device frames, MP4 export up to 4K, and GIF export.",
					"Cap's Studio mode advertises background customization, zoom effects, trimming, and 4K/60fps export. Screen Studio advertises automatic and manual zoom, cursor animation, backgrounds, cropping, speed controls, share links, and up-to-4K/60fps or GIF export.",
				],
			},
			{
				title: "Pricing and the bottom line",
				paragraphs: [
					"Cap offers a personal-use free tier, a $29/year desktop commercial license, and Cap Pro listed at $8.16 per user per month when billed annually. Screen Studio displays monthly and yearly subscription options. Clapio is free to start and Clapio Pro is a one-time $79 list-price purchase, currently shown at $59 founder pricing.",
					"Choose Screen Studio for its macOS-focused automatic workflow, Cap for instant sharing, open-source code, collaboration, or self-hosting, and Clapio for local-first cross-platform recording, SRT/VTT export, and a one-time Pro license.",
				],
			},
		],
		faqs: [
			{
				question: "Is there a version of Screen Studio for Windows or Linux?",
				answer:
					"Screen Studio's official download page currently offers Apple Silicon and Intel macOS builds. Clapio provides Windows 10/11 and Linux builds with automatic zoom, cursor treatment, backgrounds, and device frames.",
			},
			{
				question: "Do Cap and Clapio both support Linux?",
				answer:
					"Yes. Clapio publishes an AppImage and supports Wayland and X11. Cap's current download page publishes an x86_64 .deb for Debian-compatible distributions. Compare package format, capture support on your desktop session, and update workflow.",
			},
			{
				question: "Which tools can keep transcription on the device?",
				answer:
					"Clapio runs Whisper transcription locally and exports captions as SRT/VTT. Screen Studio also advertises on-device transcript generation. Cap lists transcription with its Pro AI features and offers both managed cloud and self-hosted workflows.",
			},
			{
				question: "Do all three tools have auto-zoom?",
				answer:
					"Yes — automatic zoom on clicks with smooth cursor motion is the defining feature of the category, and Screen.studio, Cap, and Clapio all offer their own take on it.",
			},
		],
		resources: [
			{
				label: "Screen Studio Alternative",
				href: "/screen-studio-alternative/",
				description: "The full landing page comparing Clapio and Screen.studio feature by feature.",
			},
			{
				label: "Cap Alternative",
				href: "/cap-alternative/",
				description:
					"The full landing page comparing Clapio and Cap, including the honest trade-offs.",
			},
			{
				label: "Screen Studio for Windows",
				href: "/screen-studio-for-windows/",
				description: "How to get the cinematic look on a PC, where Screen.studio doesn't run.",
			},
		],
		sources: [
			{
				label: "Screen Studio product and pricing",
				href: "https://screen.studio/",
				description: "Official features, transcription, export, and subscription information.",
			},
			{
				label: "Screen Studio download",
				href: "https://screen.studio/download",
				description: "Official supported downloads.",
			},
			{
				label: "Cap pricing",
				href: "https://cap.so/pricing",
				description: "Official plans, licensing, and feature comparison.",
			},
			{
				label: "Cap downloads",
				href: "https://cap.so/download",
				description: "Official current desktop packages.",
			},
			{
				label: "Cap installation docs",
				href: "https://cap.so/docs/installation",
				description: "Official platform and Linux package details.",
			},
		],
		ctaTitle: "Try the cross-platform pick",
		ctaDescription:
			"Download Clapio for macOS, Windows, or Linux — cinematic recording with on-device captions, wherever your team works.",
	},
];

function normalizeBlogPath(pathname: string): string {
	if (!pathname) {
		return "/";
	}

	if (pathname.endsWith("/index.html")) {
		return `${pathname.slice(0, -"index.html".length)}`;
	}

	return pathname.endsWith("/") ? pathname : `${pathname}/`;
}

export function isBlogHubPath(pathname: string): boolean {
	return ["/blog/", "/blog", "/blog/index.html"].includes(pathname);
}

export function getBlogPost(pathname: string): BlogPost | null {
	const normalizedPath = normalizeBlogPath(pathname);
	return BLOG_POSTS.find((post) => post.href === normalizedPath) ?? null;
}
